import { Flex, Text } from '@chakra-ui/react';
import {
  faBell,
  faCopyright,
  faEdit
} from '@fortawesome/pro-regular-svg-icons';
import { Accordion } from '@morressier/alembic';
import { IconBadge } from 'src/components/IconBadge';
import HighlightItem from '../../editorial/PeerReview/HighlightItem';
import { useSubmissionData } from 'src/contexts/SubmissionDataProvider';
import { MY_SUBMISSION_TABS } from 'src/constants';
import AdditionalInformationWrapper from '../../AdditionalInformationWrapper';
import dateFormat from 'dateformat';
import { useQuery } from '@tanstack/react-query';
import {
  fetchSubmissionContext,
  fetchSubmittingAuthorOnlyCopyrightForm
} from 'src/api/queries';
import { IRoleType } from '@publishing-engine/domain/models';
import {
  getICMJEContext,
  getIcmjeDisclosureLabel
} from '../../../../../utils/icmje';
import { isFormConfigured } from '../../../../../utils/formConfig';

interface Props {
  setActiveTab: (tab: any) => JSX.Element;
}

export const AuthorPreProductionAdditionalInfo = ({ setActiveTab }: Props) => {
  const {
    submissionId,
    changeRequestData,
    workflowResponse: {
      workflowData: { changeRequest }
    },
    submissionContextId,
    icmjeForm = [],
    icmjeFormList
  } = useSubmissionData();

  const { data, isLoading } = useQuery({
    queryKey: ['submission-context', submissionContextId],
    queryFn: () =>
      submissionContextId ? fetchSubmissionContext(submissionContextId) : null
  });

  const authorCopyrightModel =
    data && !isLoading ? data.config.authorCopyrightModel : null;

  const isOldCopyrightForm =
    isFormConfigured(authorCopyrightModel) &&
    (authorCopyrightModel === 'SubmittingAuthorOnly' ||
      authorCopyrightModel === 'SubmittingAuthorForAll');

  const authorICMJEModel =
    data && !isLoading ? data.config.authorICMJEModel : null;

  const isICMJEForm =
    isFormConfigured(authorICMJEModel) &&
    (authorICMJEModel === 'SubmittingAuthorOnly' ||
      authorICMJEModel === 'SubmittingAuthorForAll');

  const isChangeRequested = changeRequest?.requested;

  const changeRequestCount =
    changeRequestData?.changeRequest?.fields?.length || 0;
  const totalAddressedRequests = changeRequestData?.totalAddressedRequests || 0;

  const copyrightFormQueryKey = [
    'submitting-author-only-copyright-form',
    { submissionId, roleType: 'author' }
  ];

  const { data: copyrightForm } = useQuery({
    queryKey: copyrightFormQueryKey,
    queryFn: () =>
      fetchSubmittingAuthorOnlyCopyrightForm(
        submissionId,
        'author' as IRoleType
      )
  });

  return (
    <>
      {(isChangeRequested || isOldCopyrightForm || isICMJEForm) && (
        <AdditionalInformationWrapper>
          <Flex flexDirection="column">
            {/* ----- Highlights ----- */}
            <Accordion
              chakraAccordionProps={{
                width: '100%',
                defaultIndex: [0]
              }}
              items={[
                {
                  title: (
                    <Flex alignItems={'flex-start'} gap={2}>
                      <IconBadge icon={faBell} variant="neutral" />
                      <Flex flexDirection={'column'} marginLeft={2}>
                        <Text textStyle="headingItem">Highlights</Text>
                        <Text textStyle="textContext">
                          Please review key updates and other relevant
                          information in this phase.
                        </Text>
                      </Flex>
                    </Flex>
                  ),
                  content: (
                    <Flex flexDirection="column" gap={2}>
                      {isChangeRequested && (
                        <HighlightItem
                          variant="attention"
                          icon={faEdit}
                          heading={`${totalAddressedRequests}/${changeRequestCount} Change Requests Addressed`}
                          context="Address all change requests to publish this Submission."
                          buttonOnClick={() => {
                            setActiveTab?.(MY_SUBMISSION_TABS.FEEDBACK);
                          }}
                          buttonText="View Request"
                        />
                      )}
                      {isOldCopyrightForm &&
                        (copyrightForm?.authorCopyrightForm[0]?.file &&
                        copyrightForm?.authorCopyrightForm[0]?.file.url ? (
                          <HighlightItem
                            variant="success"
                            icon={faCopyright}
                            heading="Copyright and Publication Agreement Submitted"
                            context={`An agreement was submitted to the Editors${
                              copyrightForm?.authorCopyrightForm[0]?.date
                                ? ` on ${dateFormat(
                                    copyrightForm.authorCopyrightForm[0].date,
                                    'mmmm d, yyyy'
                                  )}`
                                : ''
                            }.`}
                            buttonOnClick={() => {
                              setActiveTab?.(MY_SUBMISSION_TABS.RIGHTS);
                            }}
                            buttonText="View Agreement"
                          />
                        ) : (
                          <HighlightItem
                            variant="attention"
                            icon={faCopyright}
                            heading="Copyright and Publication Agreement Missing"
                            context="An agreement is required to publish your Submission."
                            buttonOnClick={() => {
                              setActiveTab?.(MY_SUBMISSION_TABS.RIGHTS);
                            }}
                            buttonText="View Agreement"
                          />
                        ))}
                      {isICMJEForm &&
                        (authorICMJEModel === 'SubmittingAuthorOnly' ? (
                          icmjeForm?.[0]?.file?.url ? (
                            <HighlightItem
                              variant="success"
                              icon={faCopyright}
                              heading="Forms and Disclosures Submitted"
                              context={`A form was submitted to the Editors${
                                icmjeForm?.[0]?.date
                                  ? ` on ${dateFormat(
                                      icmjeForm?.[0].date,
                                      'mmmm d, yyyy'
                                    )}`
                                  : ''
                              }.`}
                              buttonOnClick={() =>
                                setActiveTab?.(MY_SUBMISSION_TABS.RIGHTS)
                              }
                              buttonText="Review Info"
                            />
                          ) : (
                            <HighlightItem
                              variant="attention"
                              icon={faCopyright}
                              heading="Forms and Disclosures Missing"
                              context="A form is required to support the Forms and Disclosures."
                              buttonOnClick={() =>
                                setActiveTab?.(MY_SUBMISSION_TABS.RIGHTS)
                              }
                              buttonText="View Form"
                            />
                          )
                        ) : authorICMJEModel === 'SubmittingAuthorForAll' &&
                          icmjeFormList?.length ? (
                          <HighlightItem
                            variant="attention"
                            icon={faCopyright}
                            heading={getIcmjeDisclosureLabel(
                              icmjeFormList,
                              icmjeForm
                            )}
                            context={getICMJEContext(icmjeForm)}
                            buttonOnClick={() => {
                              setActiveTab?.(MY_SUBMISSION_TABS.RIGHTS);
                            }}
                            buttonText={
                              icmjeForm?.[0]?.file.url
                                ? 'Review Info'
                                : 'View Form'
                            }
                          />
                        ) : null)}
                    </Flex>
                  )
                }
              ]}
            />
          </Flex>
        </AdditionalInformationWrapper>
      )}
    </>
  );
};
